<script setup>
import { useUser } from '@frontJs/composables/user';
import BellRingingIcon from '@iconify-vue/ph/bell-ringing-fill';
import GearIcon from '@iconify-vue/ph/gear';
import GearFineIcon from '@iconify-vue/ph/gear-fine-fill';
import ListIcon from '@iconify-vue/ph/list';
import ListChecksIcon from '@iconify-vue/ph/list-checks-fill';
import PhoneIcon from '@iconify-vue/ph/phone-fill';
import PhoneListIcon from '@iconify-vue/ph/phone-list-fill';
import PowerIcon from '@iconify-vue/ph/power';
import PuzzlePieceIcon from '@iconify-vue/ph/puzzle-piece-fill';
import SquaresFourIcon from '@iconify-vue/ph/squares-four-fill';
import UserCircleIcon from '@iconify-vue/ph/user-circle-fill';
import UsersIcon from '@iconify-vue/ph/users-fill';
import { Link, router, usePage } from '@inertiajs/vue3';
import FlashMessage from '@js/components/FlashMessage.vue';
import NavCollapsible from '@js/components/navigation/NavCollapsible.vue';
import NavGroup from '@js/components/navigation/NavGroup.vue';
import NavItem from '@js/components/navigation/NavItem.vue';
import { useSecurity } from '@js/composables/security';
import { hasRole } from '@js/utils/permission';
import { useRoute } from '@js/utils/route';
import { Motion } from 'motion-v';
import {
    PopoverClose,
    PopoverContent,
    PopoverPortal,
    PopoverRoot,
    PopoverTrigger,
} from 'reka-ui';
import { computed, onMounted, ref } from 'vue';

const { showCustomSidebar = null, toggleCustomSidebar = null } = defineProps({
    showCustomSidebar: {
        type: Boolean,
        required: false,
    },
    toggleCustomSidebar: {
        type: Function,
        required: false,
    },
});

const { updateMetaCsrfToken } = useSecurity();

const { route, routesAreActive } = useRoute();

const { currentUserFullName, currentUserEmail } = useUser();

const callbackRequestToProcessCount = computed(
    () => usePage().props.callbackRequestToProcessCount,
);

const alertCount = computed(() => usePage().props.alertCount);

const ticketCount = computed(() => usePage().props.ticketCount);

const phoneCallCount = computed(() => usePage().props.phoneCallCount);

const showMobileSidebar = ref(false);

function toggleMobileSidebar() {
    showMobileSidebar.value = !showMobileSidebar.value;
}

onMounted(() => {
    updateMetaCsrfToken();
});
</script>

<template>
    <FlashMessage />

    <nav
        class="fixed top-0 z-50 flex w-full items-center justify-between border-b border-gray-200 bg-white p-3"
    >
        <div class="flex items-center justify-start gap-4">
            <button
                type="button"
                class="cursor-pointer rounded-lg p-2 text-indigo-950 duration-150 hover:bg-indigo-600 hover:text-white xl:hidden"
                :class="{ 'bg-indigo-600 text-white': showMobileSidebar }"
                @click="toggleMobileSidebar"
            >
                <ListIcon class="size-6" />
            </button>

            <button
                v-if="toggleCustomSidebar"
                type="button"
                class="hidden cursor-pointer rounded-lg p-2 text-indigo-950 duration-150 hover:bg-indigo-600 hover:text-white xl:block"
                :class="{ 'bg-indigo-600 text-white': showCustomSidebar }"
                @click="toggleCustomSidebar"
            >
                <ListIcon class="size-6" />
            </button>

            <Link :href="route('front.index')">
                <img
                    src="/images/logo/logo.svg"
                    alt="Datakepler"
                    class="h-8 max-xl:hidden"
                />

                <img
                    src="/images/logo/logotype.svg"
                    alt="Datakepler"
                    class="h-8 xl:hidden"
                />
            </Link>

            <ul
                class="fixed top-[4.05rem] left-0 z-30 h-[calc(100%-65px)] w-64 gap-2 overflow-y-auto bg-indigo-950 px-3 py-4 text-white max-xl:flex-col xl:static xl:flex xl:h-full xl:w-full xl:overflow-y-visible xl:bg-white xl:px-0 xl:py-0 xl:pt-0 xl:text-black"
                :class="{ hidden: !showMobileSidebar, flex: showMobileSidebar }"
            >
                <NavItem
                    v-if="
                        hasRole('super_admin') ||
                        hasRole('admin') ||
                        hasRole('multi_account')
                    "
                    title="Tableau de bord"
                    :icon="SquaresFourIcon"
                    :href="route('front.dashboard')"
                    :active="routesAreActive(['front.dashboard'])"
                    :level="0"
                />

                <NavItem
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Modules"
                    :icon="PuzzlePieceIcon"
                    :href="route('front.modules')"
                    :active="
                        routesAreActive(['front.modules', 'front.updateModule'])
                    "
                    :level="0"
                />

                <NavItem
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Utilisateurs"
                    :icon="UsersIcon"
                    :href="route('front.users')"
                    :active="
                        routesAreActive([
                            'front.users',
                            'front.createUser',
                            'front.updateUser',
                            'front.createCustomerUser',
                            'front.updateCustomerUser',
                        ])
                    "
                    :level="0"
                />

                <NavItem
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Gestion de projet"
                    :icon="GearFineIcon"
                    :href="route('front.projectManagement.task.tasks')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.objectives',
                            'front.projectManagement.task.tasks',
                            'front.projectManagement.task.projects',
                            'front.projectManagement.task.createTask',
                            'front.projectManagement.task.createTaskIdea',
                            'front.projectManagement.task.updateTask',
                            'front.projectManagement.task.updateTaskIdea',
                            'front.projectManagement.task.taskTimes',
                            'front.projectManagement.task.tickets',
                            'front.projectManagement.task.ticket',
                            'front.projectManagement.ai.productContents',
                            'front.projectManagement.ai.productImages',
                            'front.projectManagement.ai.productImageAttachment',
                            'front.projectManagement.apikom.affiliations',
                            'front.projectManagement.apikom.createAffiliation',
                            'front.projectManagement.apikom.updateAffiliation',
                            'front.projectManagement.apikom.quotations',
                            'front.projectManagement.apikom.createQuotation',
                            'front.projectManagement.apikom.updateQuotation',
                            'front.projectManagement.apikom.invoicing',
                            'front.projectManagement.apikom.packages',
                            'front.projectManagement.apikom.package',
                            'front.projectManagement.apikom.opportunities',
                            'front.projectManagement.apikom.partners',
                            'front.projectManagement.apikom.createPartner',
                            'front.projectManagement.apikom.updatePartner',
                            'front.projectManagement.apikom.prospects',
                            'front.projectManagement.apikom.prospect',
                            'front.projectManagement.apikom.appointments',
                            'front.projectManagement.apikom.createAppointment',
                            'front.projectManagement.apikom.updateAppointment',
                            'front.projectManagement.customer.budgets',
                            'front.projectManagement.customer.contacts',
                            'front.projectManagement.customer.createCustomer',
                            'front.projectManagement.customer.updateCustomer',
                            'front.projectManagement.customer.externalServices',
                            'front.projectManagement.customer.externalServices.googleMerchant',
                            'front.projectManagement.customer.externalServices.facebook',
                            'front.projectManagement.customer.externalServices.facebookAds',
                            'front.projectManagement.customer.externalServices.instagram',
                            'front.projectManagement.customer.externalServices.hiboutik',
                            'front.projectManagement.customer.externalServices.amazon',
                            'front.projectManagement.customer.externalServices.manoMano',
                            'front.projectManagement.customer.externalServices.eCommerce',
                            'front.projectManagement.customer.externalServices.verifiedReviews',
                            'front.projectManagement.customer.externalServices.guaranteedReviewsCompany',
                            'front.projectManagement.customer.externalServices.mailjet',
                            'front.projectManagement.customer.externalServices.printNode',
                            'front.projectManagement.customer.externalServices.googleMyBusiness',
                            'front.projectManagement.customer.externalServices.googleAnalytics',
                            'front.projectManagement.customer.externalServices.googleMyBusiness',
                            'front.projectManagement.customer.externalServices.googleSearchConsole',
                            'front.projectManagement.customer.externalServices.googleAds',
                            'front.projectManagement.customer.services',
                            'front.projectManagement.customer.reports',
                            'front.projectManagement.customer.websiteVariants',
                            'front.projectManagement.customer.websiteVariant',
                            'front.projectManagement.dev.eCommerceModules',
                            'front.projectManagement.dev.pageSpeed',
                            'front.projectManagement.dev.createPageSpeedUrl',
                            'front.projectManagement.dev.servers',
                            'front.projectManagement.dev.websites',
                            'front.projectManagement.dev.website',
                            'front.projectManagement.sea.audiences',
                            'front.projectManagement.sea.audience',
                            'front.projectManagement.sea.planning',
                            'front.projectManagement.sea.planningKeywords',
                            'front.projectManagement.sea.planningKeyword',
                            'front.projectManagement.sea.productManagements',
                            'front.projectManagement.sea.productManagement',
                            'front.projectManagement.sma.productFeeds',
                            'front.projectManagement.sma.productFeed',
                            'front.projectManagement.seo.apikomContents',
                            'front.projectManagement.seo.createApikomContent',
                            'front.projectManagement.seo.updateApikomConte',
                            'front.projectManagement.seo.eCommerceCategoryContents',
                            'front.projectManagement.seo.eCommerceProductContents',
                            'front.projectManagement.seo.devSeo',
                            'front.projectManagement.seo.indexation',
                            'front.projectManagement.seo.netlinking',
                            'front.projectManagement.seo.backlink',
                            'front.projectManagement.seo.positionTracking',
                            'front.projectManagement.sem.banners',
                            'front.projectManagement.sem.banner',
                            'front.projectManagement.sem.emailings',
                            'front.projectManagement.sem.emailing',
                            'front.projectManagement.sem.shortMessageServices',
                            'front.projectManagement.sem.shortMessageService',
                            'front.projectManagement.sem.events',
                            'front.projectManagement.sem.createEvent',
                            'front.projectManagement.sem.event',
                            'front.projectManagement.sem.customerAudits',
                            'front.projectManagement.sem.customerAudit',
                            'front.projectManagement.sem.segmentations',
                            'front.projectManagement.sem.segmentation',
                            'front.projectManagement.sem.segmentationCustomers',
                            'front.projectManagement.sem.lifeTimeValues',
                            'front.projectManagement.docAndFaq',
                            'front.projectManagement.document',
                            'front.projectManagement.faqs',
                            'front.projectManagement.faq',
                            'front.projectManagement.modules.emailingAutomations',
                            'front.projectManagement.modules.emailingAutomation',
                            'front.projectManagement.modules.emailingAutomationVariable',
                            'front.projectManagement.modules.competitors',
                            'front.projectManagement.modules.competitor',
                            'front.projectManagement.modules.competitorReport',
                            'front.projectManagement.modules.modulePackages',
                            'front.projectManagement.modules.modulePackage',
                            'front.projectManagement.modules.moduleInvoicing',
                            'front.projectManagement.modules.emailings',
                            'front.projectManagement.modules.emailing',
                            'front.projectManagement.modules.emailingVariable',
                        ])
                    "
                    class="hidden xl:flex"
                    :level="0"
                />

                <NavCollapsible
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Gestion de projet"
                    :icon="GearFineIcon"
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.objectives',
                            'front.projectManagement.task.tasks',
                            'front.projectManagement.task.projects',
                            'front.projectManagement.task.createTask',
                            'front.projectManagement.task.createTaskIdea',
                            'front.projectManagement.task.updateTask',
                            'front.projectManagement.task.updateTaskIdea',
                            'front.projectManagement.task.taskTimes',
                            'front.projectManagement.task.tickets',
                            'front.projectManagement.task.ticket',
                            'front.projectManagement.ai.productContents',
                            'front.projectManagement.ai.productImages',
                            'front.projectManagement.ai.productImageAttachment',
                            'front.projectManagement.apikom.affiliations',
                            'front.projectManagement.apikom.createAffiliation',
                            'front.projectManagement.apikom.updateAffiliation',
                            'front.projectManagement.apikom.quotations',
                            'front.projectManagement.apikom.createQuotation',
                            'front.projectManagement.apikom.updateQuotation',
                            'front.projectManagement.apikom.invoicing',
                            'front.projectManagement.apikom.packages',
                            'front.projectManagement.apikom.package',
                            'front.projectManagement.apikom.opportunities',
                            'front.projectManagement.apikom.partners',
                            'front.projectManagement.apikom.createPartner',
                            'front.projectManagement.apikom.updatePartner',
                            'front.projectManagement.apikom.prospects',
                            'front.projectManagement.apikom.prospect',
                            'front.projectManagement.apikom.appointments',
                            'front.projectManagement.apikom.createAppointment',
                            'front.projectManagement.apikom.updateAppointment',
                            'front.projectManagement.customer.budgets',
                            'front.projectManagement.customer.contacts',
                            'front.projectManagement.customer.createCustomer',
                            'front.projectManagement.customer.updateCustomer',
                            'front.projectManagement.customer.externalServices',
                            'front.projectManagement.customer.externalServices.googleMerchant',
                            'front.projectManagement.customer.externalServices.facebook',
                            'front.projectManagement.customer.externalServices.facebookAds',
                            'front.projectManagement.customer.externalServices.instagram',
                            'front.projectManagement.customer.externalServices.hiboutik',
                            'front.projectManagement.customer.externalServices.amazon',
                            'front.projectManagement.customer.externalServices.manoMano',
                            'front.projectManagement.customer.externalServices.eCommerce',
                            'front.projectManagement.customer.externalServices.verifiedReviews',
                            'front.projectManagement.customer.externalServices.guaranteedReviewsCompany',
                            'front.projectManagement.customer.externalServices.mailjet',
                            'front.projectManagement.customer.externalServices.printNode',
                            'front.projectManagement.customer.externalServices.googleMyBusiness',
                            'front.projectManagement.customer.externalServices.googleAnalytics',
                            'front.projectManagement.customer.externalServices.googleMyBusiness',
                            'front.projectManagement.customer.externalServices.googleSearchConsole',
                            'front.projectManagement.customer.externalServices.googleAds',
                            'front.projectManagement.customer.services',
                            'front.projectManagement.customer.reports',
                            'front.projectManagement.customer.websiteVariants',
                            'front.projectManagement.customer.websiteVariant',
                            'front.projectManagement.dev.eCommerceModules',
                            'front.projectManagement.dev.pageSpeed',
                            'front.projectManagement.dev.createPageSpeedUrl',
                            'front.projectManagement.dev.servers',
                            'front.projectManagement.dev.websites',
                            'front.projectManagement.dev.website',
                            'front.projectManagement.sea.audiences',
                            'front.projectManagement.sea.audience',
                            'front.projectManagement.sea.planning',
                            'front.projectManagement.sea.planningKeywords',
                            'front.projectManagement.sea.planningKeyword',
                            'front.projectManagement.sea.planningKeyword',
                            'front.projectManagement.sea.productManagements',
                            'front.projectManagement.sea.productManagement',
                            'front.projectManagement.sma.productFeeds',
                            'front.projectManagement.sma.productFeed',
                            'front.projectManagement.seo.apikomContents',
                            'front.projectManagement.seo.createApikomContent',
                            'front.projectManagement.seo.updateApikomConte',
                            'front.projectManagement.seo.eCommerceCategoryContents',
                            'front.projectManagement.seo.eCommerceProductContents',
                            'front.projectManagement.seo.devSeo',
                            'front.projectManagement.seo.indexation',
                            'front.projectManagement.seo.netlinking',
                            'front.projectManagement.seo.backlink',
                            'front.projectManagement.seo.positionTracking',
                            'front.projectManagement.sem.banners',
                            'front.projectManagement.sem.banner',
                            'front.projectManagement.sem.emailings',
                            'front.projectManagement.sem.emailing',
                            'front.projectManagement.sem.shortMessageServices',
                            'front.projectManagement.sem.shortMessageService',
                            'front.projectManagement.sem.events',
                            'front.projectManagement.sem.createEvent',
                            'front.projectManagement.sem.event',
                            'front.projectManagement.sem.customerAudits',
                            'front.projectManagement.sem.customerAudit',
                            'front.projectManagement.sem.segmentations',
                            'front.projectManagement.sem.segmentation',
                            'front.projectManagement.sem.segmentationCustomers',
                            'front.projectManagement.sem.lifeTimeValues',
                            'front.projectManagement.modules.emailingAutomations',
                            'front.projectManagement.modules.emailingAutomation',
                            'front.projectManagement.modules.emailingAutomationVariable',
                            'front.projectManagement.modules.competitors',
                            'front.projectManagement.modules.competitor',
                            'front.projectManagement.modules.competitorReport',
                            'front.projectManagement.modules.modulePackages',
                            'front.projectManagement.modules.modulePackage',
                            'front.projectManagement.modules.moduleInvoicing',
                            'front.projectManagement.modules.emailings',
                            'front.projectManagement.modules.emailing',
                            'front.projectManagement.modules.emailingVariable',
                        ])
                    "
                    :level="0"
                    class="flex xl:hidden"
                >
                    <NavGroup v-slot="{ setCurrentDisclosure }">
                        <NavCollapsible
                            title="Tâches"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.task.objectives',
                                    'front.projectManagement.task.tasks',
                                    'front.projectManagement.task.projects',
                                    'front.projectManagement.task.createTask',
                                    'front.projectManagement.task.createTaskIdea',
                                    'front.projectManagement.task.updateTask',
                                    'front.projectManagement.task.updateTaskIdea',
                                    'front.projectManagement.task.taskTimes',
                                    'front.projectManagement.task.tickets',
                                    'front.projectManagement.task.ticket',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Tâches Apikom"
                                :href="
                                    route('front.projectManagement.task.tasks')
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.task.tasks',
                                        'front.projectManagement.task.createTask',
                                        'front.projectManagement.task.createTaskIdea',
                                        'front.projectManagement.task.updateTask',
                                        'front.projectManagement.task.updateTaskIdea',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Projets Apikom"
                                :href="
                                    route(
                                        'front.projectManagement.task.projects',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.task.projects',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Temps Apikom"
                                :href="
                                    route(
                                        'front.projectManagement.task.taskTimes',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.task.taskTimes',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Tickets Apikom"
                                :href="
                                    route(
                                        'front.projectManagement.task.tickets',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.task.tickets',
                                        'front.projectManagement.task.ticket',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Objectifs"
                                :href="
                                    route(
                                        'front.projectManagement.task.objectives',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.task.objectives',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="IA"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.ai.productContents',
                                    'front.projectManagement.ai.productImages',
                                    'front.projectManagement.ai.productImageAttachment',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Contenus produits"
                                :href="
                                    route(
                                        'front.projectManagement.ai.productContents',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.ai.productContents',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Images produits"
                                :href="
                                    route(
                                        'front.projectManagement.ai.productImages',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.ai.productImages',
                                        'front.projectManagement.ai.productImageAttachment',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="Apikom"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.apikom.newsArticles',
                                    'front.projectManagement.apikom.newsArticle',
                                    'front.projectManagement.apikom.newsTheme',
                                    'front.projectManagement.apikom.affiliations',
                                    'front.projectManagement.apikom.createAffiliation',
                                    'front.projectManagement.apikom.updateAffiliation',
                                    'front.projectManagement.apikom.phoneCalls',
                                    'front.projectManagement.apikom.quotations',
                                    'front.projectManagement.apikom.createQuotation',
                                    'front.projectManagement.apikom.updateQuotation',
                                    'front.projectManagement.apikom.invoicing',
                                    'front.projectManagement.apikom.packages',
                                    'front.projectManagement.apikom.package',
                                    'front.projectManagement.apikom.opportunities',
                                    'front.projectManagement.apikom.partners',
                                    'front.projectManagement.apikom.createPartner',
                                    'front.projectManagement.apikom.updatePartner',
                                    'front.projectManagement.apikom.prospects',
                                    'front.projectManagement.apikom.prospect',
                                    'front.projectManagement.apikom.appointments',
                                    'front.projectManagement.apikom.createAppointment',
                                    'front.projectManagement.apikom.updateAppointment',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Actualités"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.newsArticles',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.newsArticles',
                                        'front.projectManagement.apikom.newsArticle',
                                        'front.projectManagement.apikom.newsTheme',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Affiliations"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.affiliations',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.affiliations',
                                        'front.projectManagement.apikom.createAffiliation',
                                        'front.projectManagement.apikom.updateAffiliation',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Appels"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.phoneCalls',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.phoneCalls',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Devis"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.quotations',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.quotations',
                                        'front.projectManagement.apikom.createQuotation',
                                        'front.projectManagement.apikom.updateQuotation',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Facturation"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.invoicing',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.invoicing',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Forfaits"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.packages',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.packages',
                                        'front.projectManagement.apikom.package',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Opportunités"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.opportunities',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.opportunities',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Partenaires"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.partners',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.partners',
                                        'front.projectManagement.apikom.createPartner',
                                        'front.projectManagement.apikom.updatePartner',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Prospects"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.prospects',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.prospects',
                                        'front.projectManagement.apikom.prospect',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Rendez-vous"
                                :href="
                                    route(
                                        'front.projectManagement.apikom.appointments',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.apikom.appointments',
                                        'front.projectManagement.apikom.createAppointment',
                                        'front.projectManagement.apikom.updateAppointment',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="Clients"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.customer.budgets',
                                    'front.projectManagement.customer.contacts',
                                    'front.projectManagement.customer.createCustomer',
                                    'front.projectManagement.customer.updateCustomer',
                                    'front.projectManagement.customer.externalServices',
                                    'front.projectManagement.customer.externalServices.googleMerchant',
                                    'front.projectManagement.customer.externalServices.facebook',
                                    'front.projectManagement.customer.externalServices.facebookAds',
                                    'front.projectManagement.customer.externalServices.instagram',
                                    'front.projectManagement.customer.externalServices.hiboutik',
                                    'front.projectManagement.customer.externalServices.amazon',
                                    'front.projectManagement.customer.externalServices.manoMano',
                                    'front.projectManagement.customer.externalServices.eCommerce',
                                    'front.projectManagement.customer.externalServices.verifiedReviews',
                                    'front.projectManagement.customer.externalServices.guaranteedReviewsCompany',
                                    'front.projectManagement.customer.externalServices.mailjet',
                                    'front.projectManagement.customer.externalServices.printNode',
                                    'front.projectManagement.customer.externalServices.googleMyBusiness',
                                    'front.projectManagement.customer.externalServices.googleAnalytics',
                                    'front.projectManagement.customer.externalServices.googleMyBusiness',
                                    'front.projectManagement.customer.externalServices.googleSearchConsole',
                                    'front.projectManagement.customer.externalServices.googleAds',
                                    'front.projectManagement.customer.services',
                                    'front.projectManagement.customer.reports',
                                    'front.projectManagement.customer.websiteVariants',
                                    'front.projectManagement.customer.websiteVariant',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Budgets"
                                :href="
                                    route(
                                        'front.projectManagement.customer.budgets',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.customer.budgets',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Contacts"
                                :href="
                                    route(
                                        'front.projectManagement.customer.contacts',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.customer.contacts',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Prestations"
                                :href="
                                    route(
                                        'front.projectManagement.customer.services',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.customer.services',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Rapports"
                                :href="
                                    route(
                                        'front.projectManagement.customer.reports',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.customer.reports',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Sites"
                                :href="
                                    route(
                                        'front.projectManagement.customer.websiteVariants',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.customer.websiteVariants',
                                        'front.projectManagement.customer.websiteVariant',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Services externes"
                                :href="
                                    route(
                                        'front.projectManagement.customer.externalServices',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.customer.externalServices',
                                        'front.projectManagement.customer.externalServices.googleMerchant',
                                        'front.projectManagement.customer.externalServices.facebook',
                                        'front.projectManagement.customer.externalServices.facebookAds',
                                        'front.projectManagement.customer.externalServices.instagram',
                                        'front.projectManagement.customer.externalServices.hiboutik',
                                        'front.projectManagement.customer.externalServices.amazon',
                                        'front.projectManagement.customer.externalServices.manoMano',
                                        'front.projectManagement.customer.externalServices.eCommerce',
                                        'front.projectManagement.customer.externalServices.verifiedReviews',
                                        'front.projectManagement.customer.externalServices.guaranteedReviewsCompany',
                                        'front.projectManagement.customer.externalServices.mailjet',
                                        'front.projectManagement.customer.externalServices.printNode',
                                        'front.projectManagement.customer.externalServices.googleMyBusiness',
                                        'front.projectManagement.customer.externalServices.googleAnalytics',
                                        'front.projectManagement.customer.externalServices.googleMyBusiness',
                                        'front.projectManagement.customer.externalServices.googleSearchConsole',
                                        'front.projectManagement.customer.externalServices.googleAds',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="DEV"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.dev.eCommerceModules',
                                    'front.projectManagement.dev.pageSpeed',
                                    'front.projectManagement.dev.createPageSpeedUrl',
                                    'front.projectManagement.dev.servers',
                                    'front.projectManagement.dev.websites',
                                    'front.projectManagement.dev.website',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Modules e-commerce"
                                :href="
                                    route(
                                        'front.projectManagement.dev.eCommerceModules',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.dev.eCommerceModules',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Serveurs"
                                :href="
                                    route('front.projectManagement.dev.servers')
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.dev.servers',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Sites web"
                                :href="
                                    route(
                                        'front.projectManagement.dev.websites',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.dev.websites',
                                        'front.projectManagement.dev.website',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Temps de chargement"
                                :href="
                                    route(
                                        'front.projectManagement.dev.pageSpeed',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.dev.pageSpeed',
                                        'front.projectManagement.dev.createPageSpeedUrl',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="SEA"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.sea.audiences',
                                    'front.projectManagement.sea.audience',
                                    'front.projectManagement.sea.planning',
                                    'front.projectManagement.sea.planningKeywords',
                                    'front.projectManagement.sea.planningKeyword',
                                    'front.projectManagement.sea.productManagements',
                                    'front.projectManagement.sea.productManagement',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Audiences"
                                :href="
                                    route(
                                        'front.projectManagement.sea.audiences',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sea.audiences',
                                        'front.projectManagement.sea.audience',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Pilotage Search"
                                :href="
                                    route(
                                        'front.projectManagement.sea.keywordManagements',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sea.keywordManagements',
                                        'front.projectManagement.sea.keywordManagement',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Pilotage Shopping"
                                :href="
                                    route(
                                        'front.projectManagement.sea.productManagements',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sea.productManagements',
                                        'front.projectManagement.sea.productManagement',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Planification"
                                :href="
                                    route(
                                        'front.projectManagement.sea.planning',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sea.planning',
                                        'front.projectManagement.sea.planningKeywords',
                                        'front.projectManagement.sea.planningKeyword',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="SMA"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.sma.audiences',
                                    'front.projectManagement.sma.audience',
                                    'front.projectManagement.sma.productFeeds',
                                    'front.projectManagement.sma.productFeed',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Audiences"
                                :href="
                                    route(
                                        'front.projectManagement.sma.audiences',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sma.audiences',
                                        'front.projectManagement.sma.audience',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Flux produits"
                                :href="
                                    route(
                                        'front.projectManagement.sma.productFeeds',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sma.productFeeds',
                                        'front.projectManagement.sma.productFeed',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="SEO/GEO"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.seo.apikomContents',
                                    'front.projectManagement.seo.createApikomContent',
                                    'front.projectManagement.seo.updateApikomConte',
                                    'front.projectManagement.seo.eCommerceCategoryContents',
                                    'front.projectManagement.seo.eCommerceProductContents',
                                    'front.projectManagement.seo.devSeo',
                                    'front.projectManagement.seo.indexation',
                                    'front.projectManagement.seo.netlinking',
                                    'front.projectManagement.seo.backlink',
                                    'front.projectManagement.seo.positionTracking',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Contenus Apikom"
                                :href="
                                    route(
                                        'front.projectManagement.seo.apikomContents',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.seo.apikomContents',
                                        'front.projectManagement.seo.createApikomContent',
                                        'front.projectManagement.seo.updateApikomConte',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Contenus catégories"
                                :href="
                                    route(
                                        'front.projectManagement.seo.eCommerceCategoryContents',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.seo.eCommerceCategoryContents',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Contenus produits"
                                :href="
                                    route(
                                        'front.projectManagement.seo.eCommerceProductContents',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.seo.eCommerceProductContents',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="DEV SEO"
                                :href="
                                    route('front.projectManagement.seo.devSeo')
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.seo.devSeo',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Indexation"
                                :href="
                                    route(
                                        'front.projectManagement.seo.indexation',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.seo.indexation',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Netlinking"
                                :href="
                                    route(
                                        'front.projectManagement.seo.netlinking',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.seo.netlinking',
                                        'front.projectManagement.seo.backlink',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Suivi positions / URL"
                                :href="
                                    route(
                                        'front.projectManagement.seo.positionTracking',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.seo.positionTracking',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="SEM"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.sem.banners',
                                    'front.projectManagement.sem.banner',
                                    'front.projectManagement.sem.emailings',
                                    'front.projectManagement.sem.emailing',
                                    'front.projectManagement.sem.shortMessageServices',
                                    'front.projectManagement.sem.shortMessageService',
                                    'front.projectManagement.sem.events',
                                    'front.projectManagement.sem.createEvent',
                                    'front.projectManagement.sem.event',
                                    'front.projectManagement.sem.customerAudits',
                                    'front.projectManagement.sem.customerAudit',
                                    'front.projectManagement.sem.segmentations',
                                    'front.projectManagement.sem.segmentation',
                                    'front.projectManagement.sem.segmentationCustomers',
                                    'front.projectManagement.sem.lifeTimeValues',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Audits clients"
                                :href="
                                    route(
                                        'front.projectManagement.sem.customerAudits',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sem.customerAudits',
                                        'front.projectManagement.sem.customerAudit',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Bannières"
                                :href="
                                    route('front.projectManagement.sem.banners')
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sem.banners',
                                        'front.projectManagement.sem.banner',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Emailings"
                                :href="
                                    route(
                                        'front.projectManagement.sem.emailings',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sem.emailings',
                                        'front.projectManagement.sem.emailing',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Événements"
                                :href="
                                    route('front.projectManagement.sem.events')
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sem.events',
                                        'front.projectManagement.sem.createEvent',
                                        'front.projectManagement.sem.event',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="LTV"
                                :href="
                                    route(
                                        'front.projectManagement.sem.lifeTimeValues',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sem.lifeTimeValues',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="SMS"
                                :href="
                                    route(
                                        'front.projectManagement.sem.shortMessageServices',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sem.shortMessageServices',
                                        'front.projectManagement.sem.shortMessageService',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Segmentations"
                                :href="
                                    route(
                                        'front.projectManagement.sem.segmentations',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.sem.segmentations',
                                        'front.projectManagement.sem.segmentation',
                                        'front.projectManagement.sem.segmentationCustomers',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavCollapsible
                            title="Modules"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.modules.emailingAutomations',
                                    'front.projectManagement.modules.emailingAutomation',
                                    'front.projectManagement.modules.emailingAutomationVariable',
                                    'front.projectManagement.modules.competitors',
                                    'front.projectManagement.modules.competitor',
                                    'front.projectManagement.modules.competitorReport',
                                    'front.projectManagement.modules.modulePackages',
                                    'front.projectManagement.modules.modulePackage',
                                    'front.projectManagement.modules.moduleInvoicing',
                                    'front.projectManagement.modules.emailings',
                                    'front.projectManagement.modules.emailing',
                                    'front.projectManagement.modules.emailingVariable',
                                ])
                            "
                            :level="1"
                            :set-current-disclosure="setCurrentDisclosure"
                        >
                            <NavItem
                                title="Forfaits modules"
                                :href="
                                    route(
                                        'front.projectManagement.modules.modulePackages',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.modules.modulePackages',
                                        'front.projectManagement.modules.modulePackage',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Facturation modules"
                                :href="
                                    route(
                                        'front.projectManagement.modules.moduleInvoicing',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.modules.moduleInvoicing',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Concurrents"
                                :href="
                                    route(
                                        'front.projectManagement.modules.competitors',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.modules.competitors',
                                        'front.projectManagement.modules.competitor',
                                        'front.projectManagement.modules.competitorReport',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Emailings"
                                :href="
                                    route(
                                        'front.projectManagement.modules.emailings',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.modules.emailings',
                                        'front.projectManagement.modules.emailing',
                                        'front.projectManagement.modules.emailingVariable',
                                    ])
                                "
                                :level="2"
                            />

                            <NavItem
                                title="Emailings automatisés"
                                :href="
                                    route(
                                        'front.projectManagement.modules.emailingAutomations',
                                    )
                                "
                                :active="
                                    routesAreActive([
                                        'front.projectManagement.modules.emailingAutomations',
                                        'front.projectManagement.modules.emailingAutomation',
                                        'front.projectManagement.modules.emailingAutomationVariable',
                                    ])
                                "
                                :level="2"
                            />
                        </NavCollapsible>

                        <NavItem
                            title="Alertes"
                            :href="route('front.projectManagement.alerts')"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.alerts',
                                ])
                            "
                            :level="1"
                        />

                        <NavItem
                            :title="'Doc & FAQ'"
                            :href="route('front.projectManagement.docAndFaq')"
                            :active="
                                routesAreActive([
                                    'front.projectManagement.docAndFaq',
                                    'front.projectManagement.document',
                                    'front.projectManagement.faqs',
                                    'front.projectManagement.faq',
                                ])
                            "
                            :level="1"
                        />
                    </NavGroup>
                </NavCollapsible>

                <NavItem
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Demandes de rappel"
                    :icon="PhoneIcon"
                    :href="route('front.callbackRequests')"
                    :active="routesAreActive(['front.callbackRequests'])"
                    :level="0"
                    :pin-value="callbackRequestToProcessCount"
                    :ping-type="
                        callbackRequestToProcessCount > 0 ? 'danger' : 'success'
                    "
                    :animate-ping="callbackRequestToProcessCount > 0"
                />

                <NavItem
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Tickets Apikom"
                    :href="
                        route('front.projectManagement.task.tickets', {
                            tab: 'toBeProcessed',
                        })
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.tickets',
                        ])
                    "
                    :level="0"
                    :icon="ListChecksIcon"
                    :pin-value="ticketCount"
                    :ping-type="ticketCount === 0 ? 'success' : 'danger'"
                    :animate-ping="ticketCount !== 0"
                />

                <NavItem
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Appels"
                    :href="route('front.projectManagement.apikom.phoneCalls')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.phoneCalls',
                        ])
                    "
                    :level="0"
                    :icon="PhoneListIcon"
                    :pin-value="phoneCallCount"
                    :ping-type="phoneCallCount === 0 ? 'success' : 'danger'"
                    :animate-ping="phoneCallCount !== 0"
                />

                <NavItem
                    v-if="hasRole('super_admin') || hasRole('admin')"
                    title="Alertes"
                    :href="route('front.alerts')"
                    :active="routesAreActive(['front.alerts'])"
                    :level="0"
                    :icon="BellRingingIcon"
                    :pin-value="alertCount"
                    :ping-type="alertCount === 0 ? 'success' : 'danger'"
                    :animate-ping="alertCount !== 0"
                />
            </ul>
        </div>

        <div class="flex items-center gap-4">
            <PopoverRoot>
                <PopoverTrigger class="cursor-pointer focus:outline-hidden">
                    <UserCircleIcon class="h-10 w-10 text-gray-400" />
                </PopoverTrigger>

                <PopoverPortal>
                    <PopoverContent align="end" :side-offset="6" class="z-50">
                        <Motion
                            :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                            :animate="{ opacity: 1, scale: 1, y: 0 }"
                            :transition="{ duration: 0.15 }"
                            class="overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm"
                            :style="{
                                transformOrigin:
                                    'var(--reka-popover-content-transform-origin)',
                            }"
                        >
                            <div class="px-4 py-3">
                                <p class="text-sm text-gray-900">
                                    {{ currentUserFullName }}
                                </p>

                                <p
                                    class="truncate text-sm font-medium text-gray-900"
                                >
                                    {{ currentUserEmail }}
                                </p>
                            </div>

                            <ul class="flex flex-col gap-px bg-gray-100 pt-px">
                                <li
                                    v-if="
                                        hasRole('super_admin') ||
                                        hasRole('admin')
                                    "
                                >
                                    <PopoverClose
                                        as-child
                                        @click="
                                            router.visit(
                                                route('front.settings'),
                                            )
                                        "
                                    >
                                        <button
                                            type="button"
                                            class="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-sm text-gray-700 duration-150 hover:bg-gray-100"
                                            :class="{
                                                'bg-white': !routesAreActive([
                                                    'front.settings',
                                                ]),
                                                'bg-gray-100': routesAreActive([
                                                    'front.settings',
                                                ]),
                                            }"
                                        >
                                            <GearIcon class="size-4" />

                                            Paramètres
                                        </button>
                                    </PopoverClose>
                                </li>

                                <li>
                                    <a
                                        :href="route('front.logout')"
                                        class="flex w-full items-center gap-2 bg-white px-4 py-2 text-sm text-gray-700 duration-150 hover:bg-gray-100"
                                    >
                                        <PowerIcon class="size-4" />

                                        Déconnexion
                                    </a>
                                </li>
                            </ul>
                        </Motion>
                    </PopoverContent>
                </PopoverPortal>
            </PopoverRoot>
        </div>
    </nav>

    <div
        class="fixed top-0 left-0 z-20 block h-screen w-screen cursor-pointer bg-black opacity-50"
        :class="{ hidden: !showMobileSidebar }"
        @click="toggleMobileSidebar"
    ></div>

    <div class="min-h-screen">
        <div class="mt-16 bg-gray-100 p-4">
            <slot />
        </div>
    </div>
</template>
